import React from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

/**
 * Global ErrorBoundary — Phase 9 hardening.
 *
 * Any uncaught render error inside its subtree is caught here and replaced
 * by a French, brand-consistent fallback page with a reload button.
 * We NEVER expose a raw white page nor a stack trace to end users.
 *
 * Usage: wrap `<App />` in `index.js`.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        // Server-side logging isn't possible from the browser, but at least
        // we surface the error in dev tools for the developer. Nothing
        // sensitive is expected in these errors (they're React render bugs).
        // eslint-disable-next-line no-console
        console.error('[Nivo] ErrorBoundary caught:', error, info?.componentStack);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        return (
            <main
                data-testid="error-boundary"
                className="min-h-screen w-full bg-[#F5F6FA] flex items-center justify-center px-6 py-12"
            >
                <div className="max-w-[440px] w-full bg-white rounded-[12px] border border-[#E5E7EB] shadow-nivo-card p-8 text-center">
                    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-[#EF4444]/15 flex items-center justify-center">
                        <AlertTriangle size={24} strokeWidth={1.75} className="text-[#EF4444]" />
                    </div>
                    <h1 className="text-lg font-bold text-[#1A1D2E] mb-2">
                        Une erreur inattendue est survenue
                    </h1>
                    <p className="text-sm text-[#6B7280] mb-6">
                        Nivo a rencontré un problème. Rechargez la page pour continuer. Si le problème persiste, contactez votre administrateur.
                    </p>
                    <button
                        type="button"
                        data-testid="error-boundary-reload"
                        onClick={this.handleReload}
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold hover:opacity-95"
                    >
                        <RefreshCcw size={16} strokeWidth={2} />
                        Recharger la page
                    </button>
                </div>
            </main>
        );
    }
}
